import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, FormGroup } from 'reactstrap';
import { Alert } from '@shoflo/polymer/components/Alert/Alert';
import { Button } from '@shoflo/polymer/components/Button/Button';
import { Input } from '@shoflo/polymer/components/Input/Input';
import { InputLabel } from '@shoflo/polymer/components/InputLabel/InputLabel';
import { UserValidator } from '@shoflo/polymer/validators/UserValidator/UserValidator';
import { useForm, useFormState } from 'react-hook-form';
import styled from 'styled-components';

import AppPaths from '../../../AppPaths';
import { login } from 'actions/user';
import ModalPage from 'containers/pages/ModalPage/ModalPage';
import ModalSubHeader from '../../../components/ModalSubHeader';
import ModalFooterLink from '../../../components/Links/ModalFooterLink';
import LoginRouter from 'containers/routers/LoginRouter';

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledAlertContainer = styled.div`
  margin-bottom: 10px;
`;

const StyledFooterLinkWrapper = styled.div`
  a {
    padding: 0 8px;
  }
`;

const subHeaderProps = {
  message: "Don't have an account? ",
  to: AppPaths.SignupPage,
  linkTitle: 'Sign up.',
};

export const UnconnectedLoginPage = ({
  login,
  loginError,
}) => {
  const { control, register, handleSubmit } = useForm({
    defaultValues: { email: '', password: '' },
    mode: 'onTouched',
    reValidateMode: 'onBlur',
  });
  const { errors } = useFormState({ control });

  return (
    <LoginRouter>
      <ModalPage
        header={`Welcome back!\xa0\xa0Sign in to Shoflo`}
        subHeader={
          <ModalSubHeader {...subHeaderProps} />
        }
        footer={
          (
            <StyledFooterLinkWrapper>
              <ModalFooterLink to={AppPaths.SSOLoginPage}>
                SSO sign in
              </ModalFooterLink>
              <ModalFooterLink to={AppPaths.ForgotPasswordPage}>
                Forgot password?
              </ModalFooterLink>
            </StyledFooterLinkWrapper>
          )
        }
      >
        <Form
          onSubmit={handleSubmit(
            async ({ email, password, timeBasedToken }) => {
              login(email, password, timeBasedToken)
            }
          )}
        >
          {loginError && _getLoginErrorNode(loginError)}
          {(
            <>
              <FormGroup>
                <InputLabel for="email">Email</InputLabel>
                <Input
                  id="email"
                  data-testid="email-input"
                  placeholder="e.g. tim.berg@gmail.com"
                  error={errors.email}
                  {...register('email', {
                    required: true,
                    validate: (val) =>
                      UserValidator.email(val) || 'Uh oh! Invalid email',
                  })}
                />
              </FormGroup>
              <FormGroup>
                <InputLabel for="password">Password</InputLabel>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="Enter a password"
                  error={errors.password}
                  {...register('password', {
                    required: true,
                  })}
                />
              </FormGroup>
            </>
          )}
          <StyledButtonContainer>
            <Button type="submit" color="primary" data-testid="signInButton">
              Sign in
            </Button>
          </StyledButtonContainer>
        </Form>
      </ModalPage>
    </LoginRouter>
  );
};

export function _getLoginErrorNode(loginError) {
  if (loginError?.error?.request?.status === 429) {
    return (
      <StyledAlertContainer>
        <Alert
          type="warning"
          title={
            <div data-testid="too-many-requests">
              You have reached the maximum failed login attempts allowed within
              30 minutes. You may <a href="/forgot">reset</a> your password to
              login, or wait until the lockout expires
            </div>
          }
          size="small"
        />
      </StyledAlertContainer>
    );
  }

  return (
    <StyledAlertContainer>
      <Alert
        type="failure"
        title={`Login failed! Invalid email and/or password.`}
        size="small"
      />
    </StyledAlertContainer>
  );
}

UnconnectedLoginPage.propTypes = {
  login: PropTypes.func,
  loginError: PropTypes.object,
};

export default connect(
  ({ loginError }) => ({ loginError }),
  {
    login,
  },
)(UnconnectedLoginPage);
